import { useCallback } from 'react';

import {
  UseCustomSIPDeviceCreationProps,
  UseCustomSIPDeviceCreationReturnType,
} from './useCustomSIPDeviceCreation.decl';

import { SUCCESS_FEEDBACK_MESSAGES } from '@constants/translation.constants';
import {
  CreateSipSettingMutation,
  CreateSipSettingMutationVariables,
} from '@generated/CreateSipSettingMutation';
import { SipSettingType } from '@generated/globalTypes';
import { CREATE_SIP_SETTING_MUTATION } from '@graphql/mutations/CreateSipSettingMutation';
import { useGraphMutationWithFeedback } from '@hooks/useMutation';
import { ISIPDeviceCreationForm } from '@pages/user-profile-call-preferences/SIPDevicesSection/SIPDevices.section';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

/**
 * Hook to create a custom sip device given a handler responsible for success behaviour.
 * @param useCustomSIPDeviceCreationProps - destructured parameter
 * @returns an object holding a function responsible for creating a custom sip device and its state
 */
export const useCustomSIPDeviceCreation = ({
  onCompleted,
}: UseCustomSIPDeviceCreationProps): UseCustomSIPDeviceCreationReturnType => {
  const { userId } = useParams();
  const { t } = useTranslation();
  const [mutate, { loading }] = useGraphMutationWithFeedback<
    CreateSipSettingMutation,
    CreateSipSettingMutationVariables
  >(
    CREATE_SIP_SETTING_MUTATION,
    {
      type: 'form',
      validationErrorAsFormError: true,
    },
    {
      update(cache, { data }) {
        cache.modify({
          id: cache.identify({ __typename: 'AgentV2', ID: userId }),
          fields: {
            sipSettings(sipSettings) {
              const {
                createSipSetting: { ID, ...sipSetting },
              } = data!;

              return [
                ...(sipSettings ?? []),
                {
                  ...sipSetting,
                  __typename: 'AgentSipSetting',
                  ID,
                },
              ];
            },
          },
        });
      },
    }
  );

  const onCustomSIPDeviceSubmit = useCallback(
    async ({ username, domain }: ISIPDeviceCreationForm) => {
      const { error } = await mutate(
        {
          variables: {
            input: {
              agentID: userId,
              username,
              domain,
              isEnabled: true,
              type: SipSettingType.CustomSipSetting,
            },
          },
        },
        {
          successMessage: t(SUCCESS_FEEDBACK_MESSAGES.CREATION_SUCCESS, {
            name: t('user_profile.call_preferences.sip_creation.success_feedback_prefix'),
          }),
        }
      );

      if (error) {
        return error;
      }

      onCompleted();
      return undefined;
    },
    [mutate, onCompleted, t, userId]
  );

  return { onCustomSIPDeviceSubmit, loading };
};
